import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { SSOButton } from './SSOButton';
import { LoginErrorAlert } from './LoginErrorAlert';
import { signInWithGoogle, signInWithMicrosoft, signInWithEmail } from '../services/auth.service';
import { useAuth } from '../hooks/useAuth';

type SSOProvider = 'google' | 'microsoft';
type LoadingState = SSOProvider | 'email' | null;

const MAIL_ICON = 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z';
const LOCK_ICON = 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z';

interface FieldProps {
  id: string;
  label: string;
  type: string;
  icon: string;
  value: string;
  placeholder: string;
  autoComplete: string;
  disabled: boolean;
  onChange: (v: string) => void;
}

function LoginField({ id, label, type, icon, value, placeholder, autoComplete, disabled, onChange }: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-white">{label}</label>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-white/70">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
          </svg>
        </span>
        <input
          id={id}
          type={type}
          autoComplete={autoComplete}
          required
          disabled={disabled}
          value={value}
          onChange={(e) => { onChange(e.target.value); }}
          placeholder={placeholder}
          className="w-full rounded-lg border border-white/30 bg-white/10 py-2.5 pl-10 pr-3 text-sm text-white shadow-sm backdrop-blur-sm transition placeholder:text-gray-500 focus:border-white/60 focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40 disabled:bg-white/5 disabled:text-white/40"
        />
      </div>
    </div>
  );
}

export function LoginPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<LoadingState>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/" replace />;

  const handleSSOLogin = async (provider: SSOProvider) => {
    setError(null);
    setLoading(provider);
    const fn = provider === 'google' ? signInWithGoogle : signInWithMicrosoft;
    const result = await fn();
    if (result.success) navigate('/');
    else setError(result.error ?? 'ログインに失敗しました。');
    setLoading(null);
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading('email');
    const result = await signInWithEmail(email, password);
    if (result.success) navigate('/');
    else setError(result.error ?? 'ログインに失敗しました。');
    setLoading(null);
  };

  const isDisabled = loading !== null;

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-700 via-blue-600 to-blue-500 px-6 py-12 lg:bg-gradient-to-r lg:from-blue-700 lg:from-10% lg:via-blue-500 lg:via-65% lg:to-slate-50">
      {/* 左: ブランド（lg以上・絶対配置で青地に重ねる。カードは常に画面中央） */}
      <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-[42%] flex-col justify-between p-12 lg:flex">
        <div className="flex items-center gap-2 text-white">
          <svg className="h-8 w-8 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="text-xl font-bold tracking-wide">AIRS - 販売</span>
        </div>
        <div className="text-white">
          <h2 className="text-3xl font-bold leading-snug">次世代の<br />販売管理システム</h2>
          <p className="mt-4 max-w-sm text-sm text-blue-100">
            AI秘書 × 一元管理
          </p>
        </div>
        <p className="text-xs text-blue-200/70">ALL Information Registry System</p>
      </div>

      {/* ログインカード（常に画面中央） */}
      <div className="relative w-full max-w-md">
          <div className="mb-8 text-center lg:hidden">
            <h1 className="text-2xl font-bold text-white">AIRS - 販売</h1>
          </div>

          <div className="rounded-2xl border border-white/40 bg-white/20 p-8 shadow-2xl ring-1 ring-white/20 backdrop-blur-2xl">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white">ログイン</h2>
              <p className="mt-1 text-sm text-white/70">アカウント情報を入力してください</p>
            </div>

            {error && <div className="mb-4"><LoginErrorAlert message={error} /></div>}

            <form onSubmit={(e) => { void handleEmailLogin(e); }} className="space-y-4">
              <LoginField
                id="email" label="メールアドレス" type="email" icon={MAIL_ICON}
                value={email} placeholder="example@company.com" autoComplete="email"
                disabled={isDisabled} onChange={setEmail}
              />
              <LoginField
                id="password" label="パスワード" type="password" icon={LOCK_ICON}
                value={password} placeholder="パスワードを入力" autoComplete="current-password"
                disabled={isDisabled} onChange={setPassword}
              />
              <button
                type="submit"
                disabled={isDisabled}
                className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading === 'email' ? 'ログイン中...' : 'ログイン'}
              </button>
            </form>

            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-white/30" />
              <span className="text-xs text-white/70">または</span>
              <div className="h-px flex-1 bg-white/30" />
            </div>

            <div className="flex flex-col gap-3">
              <SSOButton provider="google" isLoading={loading === 'google'} onClick={() => { void handleSSOLogin('google'); }} />
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-white/70">
            ログインすることで利用規約に同意したものとみなされます。
          </p>
      </div>
    </div>
  );
}
